import { ACHIEVEMENTS } from '@/config/achievements';
import { SECTORS } from '@/config/sectors';
import { STARTING_WEAPON } from '@/config/weapons';

/**
 * Account-wide META progress (EPIC D3/D6) — the only thing that survives a
 * sector run besides pilot XP (core/progress.ts) and records: cleared
 * sectors, achievements, best clear times, deaths per sector and the last
 * picked starting weapon. One global key, like core/progress.ts.
 *
 * Migration: every field is optional on disk; `loadMeta` fills defaults so
 * older (or partial) blobs load, and unknown ids are kept untouched.
 */
export interface MetaData {
  /** Sector ids won at least once */
  cleared: string[];
  /** Earned achievement ids */
  achievements: string[];
  /** Best clear fight time per sector id (s) */
  bestTimes: Record<string, number>;
  /** Failed sector runs per sector id (the "deathless" achievement) */
  deaths: Record<string, number>;
  /** Last starting weapon picked on the sector select screen */
  startWeapon: string;
  /** Feats met per sector id (achievement ids) — the sector card badges */
  medals: Record<string, string[]>;
}

const STORAGE_KEY = 'bosshot-meta-v1';

let cache: MetaData | null = null;

function defaults(): MetaData {
  return {
    cleared: [], achievements: [], bestTimes: {}, deaths: {}, startWeapon: STARTING_WEAPON,
    medals: {},
  };
}

export function loadMeta(): MetaData {
  if (cache) return cache;
  const meta = defaults();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const disk = raw ? (JSON.parse(raw) as Partial<MetaData>) : {};
    if (Array.isArray(disk.cleared)) meta.cleared = disk.cleared.filter((s) => typeof s === 'string');
    if (Array.isArray(disk.achievements)) {
      meta.achievements = disk.achievements.filter((s) => typeof s === 'string');
    }
    if (disk.bestTimes && typeof disk.bestTimes === 'object') meta.bestTimes = { ...disk.bestTimes };
    if (disk.deaths && typeof disk.deaths === 'object') meta.deaths = { ...disk.deaths };
    if (typeof disk.startWeapon === 'string') meta.startWeapon = disk.startWeapon;
    if (disk.medals && typeof disk.medals === 'object') meta.medals = { ...disk.medals };
  } catch {
    /* storage unavailable or corrupt — start fresh */
  }
  cache = meta;
  return meta;
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loadMeta()));
  } catch {
    /* storage unavailable — ignore */
  }
}

/** Sectors open on the select screen: the first + one past every clear. */
export function unlockedSectorCount(): number {
  const meta = loadMeta();
  let open = 1;
  SECTORS.forEach((s, i) => {
    if (meta.cleared.includes(s.id)) open = Math.max(open, i + 2);
  });
  return Math.min(SECTORS.length, open);
}

export function isSectorUnlocked(index: number): boolean {
  return index < unlockedSectorCount();
}

export function isSectorCleared(index: number): boolean {
  const s = SECTORS[index];
  return s !== undefined && loadMeta().cleared.includes(s.id);
}

export function hasAchievement(id: string): boolean {
  return loadMeta().achievements.includes(id);
}

/** @returns true when `id` was NOT earned before (newly unlocked now) */
export function awardAchievement(id: string): boolean {
  const meta = loadMeta();
  if (meta.achievements.includes(id)) return false;
  meta.achievements.push(id);
  persist();
  return true;
}

/** ENDLESS mode opens once an achievement rewards it (last sector clear). */
export function isEndlessUnlocked(): boolean {
  return ACHIEVEMENTS.some((a) =>
    hasAchievement(a.id) && a.rewards.some((r) => r.kind === 'endless'));
}

/** Starting weapons on offer: the default + every earned weapon reward. */
export function startWeaponChoices(): string[] {
  const out = [STARTING_WEAPON];
  for (const a of ACHIEVEMENTS) {
    if (!hasAchievement(a.id)) continue;
    for (const r of a.rewards) if (r.kind === 'weapon' && !out.includes(r.id)) out.push(r.id);
  }
  return out;
}

/** Cards still held back from the shop pool (their achievement is unearned). */
export function lockedCardIds(): string[] {
  const out: string[] = [];
  for (const a of ACHIEVEMENTS) {
    if (hasAchievement(a.id)) continue;
    for (const r of a.rewards) if (r.kind === 'card') out.push(r.id);
  }
  return out;
}

export function setStartWeapon(id: string): void {
  loadMeta().startWeapon = id;
  persist();
}

/** The remembered pick, if it is still on offer. */
export function startWeapon(): string {
  const pick = loadMeta().startWeapon;
  return startWeaponChoices().includes(pick) ? pick : STARTING_WEAPON;
}

/**
 * Record a sector win.
 * @returns whether the clear time beat the stored best (first clear counts)
 */
export function recordSectorClear(index: number, seconds: number): boolean {
  const meta = loadMeta();
  const id = SECTORS[index]!.id;
  if (!meta.cleared.includes(id)) meta.cleared.push(id);
  const best = meta.bestTimes[id];
  const improved = best === undefined || seconds < best;
  if (improved) meta.bestTimes[id] = Math.round(seconds);
  persist();
  return improved;
}

export function recordSectorDeath(index: number): void {
  const meta = loadMeta();
  const id = SECTORS[index]!.id;
  meta.deaths[id] = (meta.deaths[id] ?? 0) + 1;
  persist();
}

export function sectorDeaths(index: number): number {
  const s = SECTORS[index];
  return s ? loadMeta().deaths[s.id] ?? 0 : 0;
}

/** Mark feat `id` as met in sector `index` (shown as a card badge). */
export function addMedal(index: number, id: string): void {
  const meta = loadMeta();
  const key = SECTORS[index]!.id;
  const list = (meta.medals[key] ??= []);
  if (list.includes(id)) return;
  list.push(id);
  persist();
}

export function sectorMedals(index: number): ReadonlyArray<string> {
  const s = SECTORS[index];
  return s ? loadMeta().medals[s.id] ?? [] : [];
}

export function bestTime(index: number): number | null {
  const s = SECTORS[index];
  return s ? loadMeta().bestTimes[s.id] ?? null : null;
}
